import "./index.css";
import React, { useState, useEffect, Fragment, useRef } from "react";
import Header from "../../components/Header";
import Firebase from "../../firebasehelper";
import Select from "react-select";
import { selectStyles, navTabOptions } from "./constants";
import OnboardingSitePreview from "./components/OnboardingSitePreview";
import OnboardingSiteTabs from "./components/OnboardingSiteTabs";
import ButtonRow from "./components/ButtonRow";
import { IMAGE_UPLOAD } from "../../Constants/types";
import { getFileBase64, getFilePath } from "../../Utils/utils";

export default function OnboardingSite({ location, history }) {
  const inputRef = useRef();

  const [isLoading, setLoading] = useState(true);
  const [isSaving, setSaving] = useState(false);
  const [selectedTab, setSelectedTab] = useState("landing"); // landing | layers | pages
  const [adminProfile, setAdminProfile] = useState(null);
  const [brandData, setBrandData] = useState(null);
  const [selectOptions, setSelectOptions] = useState(navTabOptions);
  const [preview, setPreview] = useState(null);
  const [userInput, setUserInput] = useState({
    description: "",
    media: null,
    navTabs: [],
  });

  const filterAvailableOptions = (navTabsInUse, navTabOptions) => {
    if (navTabsInUse.length > 0) {
      let availableOptions = navTabOptions.filter((option) =>
        navTabsInUse.every((tab) => tab.value !== option.value)
      );

      // add default None option
      availableOptions.push({ label: "None", value: "" });
      setSelectOptions(availableOptions);
    }
  };

  useEffect(() => {
    if (!location.state) {
      history.push("/");
      return;
    }

    const adminData = {
      brandId: location.state.brand,
      role: location.state.role,
      username: location.state.username,
      type: location.state.type,
      uid: localStorage.getItem("uid"),
    };
    setAdminProfile(adminData);

    Firebase.getBrandDataByID(adminData.brandId, (res) => {
      if (res) {
        setBrandData(res);
        const { onboardingSite } = res;

        const media = onboardingSite ? onboardingSite.media : "";
        const navTabs =
          onboardingSite && onboardingSite.navTabs
            ? onboardingSite.navTabs
            : [];

        media && setPreview(media);
        setUserInput((prevUserInput) => ({
          ...prevUserInput,
          description: res.description,
          navTabs: navTabs,
        }));

        filterAvailableOptions(navTabs, navTabOptions);
      }
      setLoading(false);
    });
  }, []);

  const updateState = (key, value) => {
    setUserInput((prevUserInput) => ({ ...prevUserInput, [key]: value }));
  };

  const updateTabs = (index, value) => {
    const newNavTabs = [...userInput.navTabs];
    newNavTabs[index] = value;

    filterAvailableOptions(newNavTabs, navTabOptions);

    setUserInput((prevUserInput) => ({
      ...prevUserInput,
      navTabs: newNavTabs,
    }));
  };

  const updateMedia = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();

      reader.onloadend = () => setPreview(reader.result);
      reader.onerror = () => console.error("Error reading file");

      reader.readAsDataURL(file);
      updateState("media", file);
    }
  };

  const openFileBrowser = () => {
    inputRef.current && inputRef.current.click();
  };

  const uploadFile = async () => {
    const assetTitle = new Date().getTime().toString();
    const fileBase64 = await getFileBase64(userInput.media);
    const path = `onboardingSite/${adminProfile.brandId}/images`;

    const mediaUrl = await Firebase.uploadImage(fileBase64, assetTitle, path);
    return mediaUrl;
  };

  const deleteOldFile = async () => {
    const { onboardingSite } = brandData;
    const media = onboardingSite ? onboardingSite.media : null;
    if (!media) return;

    try {
      const path = getFilePath("onboardingSite", media, IMAGE_UPLOAD);
      await Firebase.deleteFile(path);
    } catch (e) {
      console.error("deleteOldFile() error", e);
    }
  };

  const onSave = async () => {
    setSaving(true);

    let updatedData = {
      description: userInput.description,
      onboardingSite: {
        navTabs: userInput.navTabs || [],
        media: preview ? preview : null,
      },
    };

    try {
      // if new file was selected...
      if (userInput.media) {
        await deleteOldFile();
        const mediaUrl = await uploadFile();
        updatedData.onboardingSite.media = mediaUrl;
      }

      await Firebase.updateBrandDataById(adminProfile.brandId, {
        ...updatedData,
      });

      setUserInput((prevUserInput) => ({ ...prevUserInput, media: null }));
      setSaving(false);
    } catch (e) {
      console.error("onSave() error", e);
      setSaving(false);
    }
  };

  return isLoading ? (
    <main className="registration__fade-loader">
      <div className="registration__loading-brand-icon" />
      <i className="fa fa-3x fa-sync fa-spin text-muted" />
    </main>
  ) : (
    <Fragment>
      <Header
        username={adminProfile.username}
        role={adminProfile.role}
        brand={adminProfile.brandId}
        logo={brandData.logo}
        brandName={brandData.name}
      />
      <main className="onboarding">
        <div className="onboarding__title-container">
          <h1 className="onboarding__title">
            Create your very own <br className="onboarding__title-line-break" />
            onboarding site
          </h1>
        </div>
        <ButtonRow isSaving={isSaving} onSave={onSave} brandData={brandData} />
        <OnboardingSiteTabs
          selectedTab={selectedTab}
          onClick={setSelectedTab}
        />
        {selectedTab === "landing" ? (
          <div className="onboarding__wrapper">
            <section className="onboarding__settings">
              <label htmlFor="description" className="onboarding__label">
                Brand description
              </label>
              <input
                name="description"
                id="description"
                className="form-control form-control-md form-control-alt brand-registered-input onboarding__input"
                value={userInput.description}
                onChange={(e) => updateState("description", e.target.value)}
              />

              <label htmlFor="media" className="onboarding__label">
                Leading site media
              </label>
              <div className="onboarding__file-input">
                <button
                  className="btn btn-white btn-brand-hex"
                  onClick={openFileBrowser}
                  style={{
                    backgroundColor: brandData.hex || "white",
                    color: brandData.white ? "white" : "black",
                    "--hover-color": "white",
                    "--hover-text-color": "black",
                  }}
                >
                  Choose file
                </button>
                <input
                  style={{ display: "none" }}
                  name="media"
                  id="media"
                  ref={inputRef}
                  accept="image/*"
                  type="file"
                  className="form-control form-control-md form-control-alt brand-registered-input"
                  onChange={updateMedia}
                />
              </div>

              <h3 className="onboarding__sub-title">Navigation</h3>
              <div className="onboarding__select-wrapper">
                {Array(6)
                  .fill(0)
                  .map((_item, i) => (
                    <div
                      className="onboarding__select-container"
                      key={`tab-${i + 1}`}
                    >
                      <label
                        htmlFor={`tab-${i + 1}`}
                        className="onboarding__select-label"
                      >{`Tab ${i + 1}`}</label>
                      <Select
                        id={`tab-${i + 1}`}
                        name={`tab-${i + 1}`}
                        value={userInput.navTabs[i]}
                        onChange={(option) => updateTabs(i, option)}
                        options={selectOptions}
                        styles={selectStyles(brandData.hex, brandData.white)}
                        placeholder="None"
                      />
                    </div>
                  ))}
              </div>
            </section>
            <OnboardingSitePreview
              userInput={userInput}
              brandData={brandData}
              preview={preview}
            />
          </div>
        ) : (
          <section className="onboarding__placeholder">
            <p>Coming soon 🏗️</p>
          </section>
        )}
      </main>
    </Fragment>
  );
}



// WEBPACK FOOTER //
// ./src/screens/OnboardingSite/index.js